import React, { useMemo, useRef } from 'react';
import { useStore } from '../context/StoreContext';
import { Tag, X, ChevronRight, ChevronLeft, Sparkles, Filter } from 'lucide-react';
import { getContrastTextColor } from '../utils/tagColors';
import { getProductLocalizedTag } from '../utils/localization';

interface CategoryTagItem {
  key: string;
  text: string;
  color: string;
  count: number;
  isCustomBadge?: boolean;
}

interface CategoryTagsMenuProps {
  categorySlug?: string;
  className?: string;
}

export const CategoryTagsMenu: React.FC<CategoryTagsMenuProps> = ({ 
  categorySlug,
  className = '' 
}) => {
  const { 
    products, 
    activeCategoryFilter, 
    activeTagFilter, 
    setActiveTagFilter, 
    savedCustomTags,
    language 
  } = useStore();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Effective category: prop or current active category
  const currentCategory = categorySlug || activeCategoryFilter;

  // Active products in this category
  const categoryProducts = useMemo(() => {
    return products.filter(p => 
      !p.inRecycleBin && 
      !p.unlisted && 
      (currentCategory === 'all' || p.category === currentCategory)
    );
  }, [products, currentCategory]);

  // Aggregate all unique tags present on products in this category
  const categoryTags: CategoryTagItem[] = useMemo(() => {
    const map = new Map<string, CategoryTagItem>();

    categoryProducts.forEach(p => {
      // 1. Check custom highlight tag
      if (p.customTag && p.customTag.text && p.customTag.text.trim()) {
        const text = p.customTag.text.trim();
        const key = text.toUpperCase();
        const existing = map.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          map.set(key, {
            key,
            text,
            color: p.customTag.color || '#E21B24',
            count: 1,
            isCustomBadge: true
          });
        }
      }

      // 2. Check general catalog tags array
      if (Array.isArray(p.tags)) {
        p.tags.forEach(rawTag => {
          const text = rawTag ? rawTag.trim() : '';
          if (!text) return;
          const key = text.toUpperCase();
          const existing = map.get(key);
          if (existing) {
            // Only increment if not already counted via customTag for this same product
            const alreadyHasCustom = p.customTag?.text?.trim().toUpperCase() === key;
            if (!alreadyHasCustom) {
              existing.count += 1;
            }
          } else {
            // Find saved color if exists
            const saved = (savedCustomTags || []).find(st => st.text.trim().toUpperCase() === key);
            map.set(key, {
              key,
              text,
              color: saved?.color || '#3B82F6',
              count: 1,
              isCustomBadge: false
            });
          }
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [categoryProducts, savedCustomTags]);

  // Scroll controls for mobile / overflow
  const handleScroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    const scrollAmount = 220;
    scrollContainerRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  // If there are no tags for products in this category, don't take up space
  if (categoryTags.length === 0) {
    return null;
  }

  const isRtl = language === 'ar';

  return (
    <div 
      className={`category-tags-menu bg-white border border-neutral-200/90 rounded-sm shadow-2xs p-2.5 sm:p-3 transition-all ${className}`}
      aria-label="Category Tags Filter"
    >
      <div className="flex items-center gap-2">
        {/* Leading Label & Tag Icon */}
        <div className="flex items-center gap-1.5 shrink-0 pr-2 border-r border-neutral-200 text-neutral-700">
          <Tag className="w-3.5 h-3.5 text-[#E21B24]" />
          <span className="text-[11px] font-black uppercase tracking-wider hidden sm:inline-block">
            {isRtl ? 'الوسوم:' : 'Tags:'}
          </span>
        </div>

        {/* Scroll Left Button */}
        <button
          type="button"
          onClick={() => handleScroll(isRtl ? 'right' : 'left')}
          className="hidden md:flex items-center justify-center p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700 shrink-0"
          aria-label="Scroll tags left"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Scrollable Tag Chips Carousel */}
        <div 
          ref={scrollContainerRef}
          className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5 flex-1 scroll-smooth"
        >
          {/* 'All Products' chip */}
          <button
            type="button"
            onClick={() => setActiveTagFilter(null)}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap shrink-0 transition-all ${
              !activeTagFilter
                ? 'bg-neutral-900 text-white shadow-xs'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 hover:text-neutral-900'
            }`}
          >
            <span>{isRtl ? 'الكل' : 'All'}</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
              !activeTagFilter ? 'bg-neutral-800 text-neutral-200' : 'bg-neutral-200 text-neutral-600'
            }`}>
              {categoryProducts.length}
            </span>
          </button>

          {/* Individual Tag Chips */}
          {categoryTags.map((tag) => {
            const isSelected = activeTagFilter?.trim().toUpperCase() === tag.key;

            return (
              <button
                key={tag.key}
                type="button"
                onClick={() => {
                  if (isSelected) {
                    setActiveTagFilter(null);
                  } else {
                    setActiveTagFilter(tag.text);
                  }
                }}
                className={`group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap shrink-0 transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-[#E21B24] text-white shadow-xs ring-2 ring-red-300'
                    : 'bg-neutral-50 hover:bg-neutral-100 text-neutral-800 border border-neutral-200/80 hover:border-neutral-300'
                }`}
                title={`Filter products tagged "${tag.text}" (${tag.count} available)`}
              >
                {/* Colored dot indicator */}
                <span 
                  className={`w-2 h-2 rounded-full shrink-0 transition-transform ${
                    isSelected ? 'bg-white scale-110' : ''
                  }`}
                  style={{ backgroundColor: isSelected ? '#FFFFFF' : tag.color }}
                />

                {/* Tag label */}
                <span className="font-semibold">{getProductLocalizedTag(tag.text, language)}</span>

                {/* Product count */}
                <span 
                  className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                    isSelected 
                      ? 'bg-red-800/60 text-white' 
                      : 'bg-neutral-200 text-neutral-600 group-hover:bg-neutral-300'
                  }`}
                >
                  {tag.count}
                </span>

                {/* Quick unselect X icon if active */}
                {isSelected && (
                  <X className="w-3 h-3 text-white/90 hover:text-white ml-0.5" />
                )}
              </button>
            );
          })}
        </div>

        {/* Scroll Right Button */}
        <button
          type="button"
          onClick={() => handleScroll(isRtl ? 'left' : 'right')}
          className="hidden md:flex items-center justify-center p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700 shrink-0"
          aria-label="Scroll tags right"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Clear active filter quick button if any tag is selected */}
        {activeTagFilter && (
          <button
            type="button"
            onClick={() => setActiveTagFilter(null)}
            className="flex items-center gap-1 text-[11px] font-bold text-neutral-500 hover:text-[#E21B24] px-2 py-1 rounded hover:bg-red-50 transition-colors shrink-0 whitespace-nowrap"
            title="Clear tag filter"
          >
            <X className="w-3 h-3" />
            <span className="hidden sm:inline">{isRtl ? 'إلغاء التصفية' : 'Reset'}</span>
          </button>
        )}
      </div>
    </div>
  );
};
